import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Pizza, CheckCircle2, XCircle, ArrowRight, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import api from "../lib/api";
import LanguageSwitcher from "../components/LanguageSwitcher";

export default function VerifyEmail() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [status, setStatus] = useState("loading"); // loading | ok | error
  const [message, setMessage] = useState("");
  const [resendEmail, setResendEmail] = useState("");
  const [showResend, setShowResend] = useState(false);
  const ranRef = useRef(false);

  useEffect(() => {
    if (ranRef.current) return; // evita doble invocación (StrictMode) que consumiría el token dos veces
    ranRef.current = true;
    if (!token) { setStatus("error"); setMessage(t("verifyEmail.invalidLink")); return; }
    (async () => {
      try {
        const { data } = await api.post("/auth/verify-email", { token });
        setStatus("ok");
        setMessage(data.message || t("verifyEmail.success"));
      } catch (err) {
        setStatus("error");
        setMessage(err?.response?.data?.detail || t("verifyEmail.invalidLink"));
      }
    })();
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-slate-50 relative">
      <div className="absolute top-4 right-4"><LanguageSwitcher variant="login" /></div>
      <div className="w-full max-w-md bg-white border border-slate-200 rounded-2xl p-8 shadow-sm text-center" data-testid="verify-email-page">
        <div className="flex items-center justify-center gap-2.5 mb-6">
          <div className="w-10 h-10 rounded-xl bg-[#FFDF00] flex items-center justify-center">
            <Pizza className="w-6 h-6 text-[#1A1A1A]" strokeWidth={2.5} />
          </div>
          <div className="font-display font-extrabold text-xl">Chizy Chiz</div>
        </div>

        {status === "loading" && (
          <div data-testid="verify-loading" className="py-6">
            <Loader2 className="w-12 h-12 text-slate-400 mx-auto animate-spin" />
            <p className="text-slate-500 mt-3 text-sm">{t("verifyEmail.checking")}</p>
          </div>
        )}

        {status === "ok" && (
          <div data-testid="verify-success" className="py-4">
            <CheckCircle2 className="w-14 h-14 text-emerald-500 mx-auto mb-3" />
            <h2 className="font-display font-extrabold text-xl text-[#1A1A1A]">{t("verifyEmail.success")}</h2>
            <p className="text-slate-500 mt-1 text-sm">{message}</p>
            <button
              data-testid="verify-go-login"
              onClick={() => navigate("/login")}
              className="mt-5 btn-primary-chizy py-3 px-6 rounded-xl inline-flex items-center gap-2"
            >
              {t("verifyEmail.goLogin")} <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        )}

        {status === "error" && (
          <div data-testid="verify-error" className="py-4">
            <XCircle className="w-14 h-14 text-red-500 mx-auto mb-3" />
            <h2 className="font-display font-extrabold text-xl text-[#1A1A1A]">{t("verifyEmail.errorTitle")}</h2>
            <p className="text-slate-500 mt-1 text-sm">{message}</p>
            <button
              data-testid="verify-request-new"
              onClick={() => navigate("/login")}
              className="mt-5 text-sm font-semibold text-slate-600 underline decoration-[#FFDF00] decoration-2 underline-offset-2"
            >
              {t("verifyEmail.backToLogin")}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
